import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CButton, CForm, CAlert } from "@coreui/react";
import logoImg from "../../assets/Images/White_Logo.svg";
import { BASE_URL } from "../constant_api";

const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 5 * 60; // 5 minutes
const RESEND_COOLDOWN_SECONDS = 30;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const inputRefs = useRef([]);

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [otpExpiresAt, setOtpExpiresAt] = useState(
    () => Date.now() + OTP_EXPIRY_SECONDS * 1000,
  );
  const [remainingSeconds, setRemainingSeconds] = useState(OTP_EXPIRY_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // ── OTP expiry countdown ──────────────────────────────────────────────────
  useEffect(() => {
    if (!email) return;

    const tick = () => {
      const secs = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
      setRemainingSeconds(secs);
      if (secs <= 0) {
        setErrorMessage("Your verification code has expired. Please request a new one.");
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [email, otpExpiresAt]);

  // ── Resend cooldown countdown ─────────────────────────────────────────────
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const digit = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    setErrorMessage("");

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();

    const pastedOtp = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pastedOtp) return;

    const newOtp = Array(OTP_LENGTH)
      .fill("")
      .map((_, index) => pastedOtp[index] || "");

    setOtp(newOtp);
    setErrorMessage("");

    const focusIndex = Math.min(pastedOtp.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async (event) => {
    event.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    if (remainingSeconds <= 0) {
      setErrorMessage("Your verification code has expired. Please request a new one.");
      return;
    }

    const enteredOtp = otp.join("");

    if (enteredOtp.length !== OTP_LENGTH) {
      setErrorMessage(`Please enter the ${OTP_LENGTH}-digit verification code.`);
      return;
    }

    if (!email) {
      setErrorMessage("Email address is missing.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/user/verify-email-otp`,
        {
          EmailAddress: email.trim().toLowerCase(),
          OTP: enteredOtp,
        }
      );

      if (!response.data?.success) {
        setErrorMessage(response.data?.message || "Unable to verify email.");
        return;
      }

      const userData = response.data?.data || {};
      const token = userData?.token;

      if (!token) {
        setErrorMessage("Email verified, but authentication token was not received.");
        return;
      }

      localStorage.setItem("userDetail", JSON.stringify(response.data.data));
      localStorage.setItem("currentLoginUserId", JSON.stringify(response.data.data.UserID));
      localStorage.setItem("OrganisationID", "");
      setSuccessMessage(response.data?.message || "Email verified successfully.");

      setTimeout(() => {
        navigate("/query", { replace: true });
      }, 800);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Invalid verification code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading || !email) return;

    setResendLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(`${BASE_URL}/user/resend-email-otp`, {
        EmailAddress: email.trim().toLowerCase(),
      });

      const expiresInSeconds = response.data?.expiresIn ?? OTP_EXPIRY_SECONDS;
      setOtpExpiresAt(Date.now() + expiresInSeconds * 1000);
      setOtp(Array(OTP_LENGTH).fill(""));
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setSuccessMessage("A new verification code has been emailed to you.");
      inputRefs.current[0]?.focus();
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Could not resend the code. Please try again."
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="qqve-login-page">
      {/* ───────── Left: brand / pitch panel ───────── */}
      <div className="qqve-left">
        <div>
          <div className="qqve-brand">
            <img src={logoImg} alt="QuickQuery" className="qqve-logo" />
          </div>

          <h1 className="qqve-headline">Automate your account mapping in minutes</h1>
          <p className="qqve-subtext">
            Configure standard expense categories, run clean multi-factor
            compliance reporting, and synchronize seamlessly to your general
            ledger.
          </p>

          {/* <div className="qqve-card">
            <div className="qqve-card-head">
              <span className="qqve-card-head-left">Active Integrations</span>
              <span className="qqve-online-badge">ONLINE</span>
            </div>
            <div className="qqve-card-stats">
              <div>
                <div className="qqve-card-stat-label">MAPPED ACCOUNTS</div>
                <div className="qqve-card-stat-value">14,204</div>
              </div>
              <div>
                <div className="qqve-card-stat-label">SYNC ACCURACY</div>
                <div className="qqve-card-stat-value qqve-green">99.98%</div>
              </div>
            </div>
          </div> */}
        </div>

        {/* <div className="qqve-footer-stats">
          <div>
            <div className="qqve-footer-stat-value">10k+</div>
            <div className="qqve-footer-stat-label">HAPPY ENTERPRISE TEAMS</div>
          </div>
          <div>
            <div className="qqve-footer-stat-value">$2.4B+</div>
            <div className="qqve-footer-stat-label">MAPPED TRANSACTIONS</div>
          </div>
          <div>
            <div className="qqve-footer-stat-value">12 mins</div>
            <div className="qqve-footer-stat-label">AVERAGE SETUP TIME</div>
          </div>
        </div> */}
      </div>

      {/* ───────── Right: form panel ───────── */}
      <div className="qqve-right">
        <div className="qqve-verify-card">
          {!email ? (
            <div className="text-center">
              <h2 className="qqve-verify-title">Email Address Missing</h2>
              <CAlert color="warning" className="mt-3 mb-0">
                Please go back to registration and try again.
              </CAlert>
              <CButton
                className="qqve-submit-btn"
                onClick={() => navigate("/register")}
              >
                Back to Register
              </CButton>
            </div>
          ) : (
            <>
              <h2 className="qqve-verify-title">Verify your email</h2>
              <p className="qqve-verify-subtitle">
                We sent a {OTP_LENGTH}-digit verification code to{" "}
                <strong>{email}</strong>. Enter the code to unlock your account.
              </p>

              {errorMessage && (
                <CAlert
                  color="warning"
                  dismissible
                  onClose={() => setErrorMessage("")}
                  className="mt-3 mb-0"
                >
                  {errorMessage}
                </CAlert>
              )}
              {successMessage && (
                <CAlert
                  color="success"
                  dismissible
                  onClose={() => setSuccessMessage("")}
                  className="mt-3 mb-0"
                >
                  {successMessage}
                </CAlert>
              )}

              <CForm noValidate onSubmit={handleVerify}>
                <div className="qqve-otp-row" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      id={`otp-${i}`}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      inputMode="numeric"
                      maxLength={1}
                      disabled={loading || remainingSeconds <= 0}
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      className="qqve-otp-box"
                    />
                  ))}
                </div>

                <div className="qqve-otp-meta">
                  {remainingSeconds > 0 ? (
                    <span>Code expires in <strong>{formatCountdown(remainingSeconds)}</strong></span>
                  ) : (
                    <span style={{ color: "#e55353", fontWeight: 600 }}>Code expired</span>
                  )}
                </div>

                <CButton
                  type="submit"
                  className="qqve-submit-btn"
                   disabled={
                    loading ||
                    remainingSeconds <= 0 ||
                    otp.some((digit) => !digit)
                  }
                >
                  {loading ? "Verifying..." : "Confirm Code"}
                </CButton>

                <div className="qqve-otp-meta">
                  Didn't receive the email?{" "}
                  {resendCooldown > 0 ? (
                    <span className="qqve-otp-muted">Resend in {formatCountdown(resendCooldown)}</span>
                  ) : (
                    <span className="qqve-otp-link" onClick={handleResend}>
                      {resendLoading ? "Sending..." : "Resend Code"}
                    </span>
                  )}
                </div>

                <div className="qqve-otp-meta">
                  Wrong details?{" "}
                  <span className="qqve-otp-link" onClick={() => navigate("/register")}>
                    Back to Register
                  </span>
                </div>
              </CForm>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;